import JSZip from 'jszip';
import { Project, TranslationFile } from '../types';

/**
 * Project Storage Utility
 * Handles exporting and importing projects as ZIP files
 */

interface ProjectBundle {
    project: Project;
    files: TranslationFile[];
}

interface ProjectJson {
    id: string;
    name: string;
    humanBenchmark?: string;
    createdAt: string;
    lastModified: string;
    version: string;
}

interface ConfigJson {
    systemPrompt: string;
    terminologyRules: string;
}

/**
 * Export a project and its files to a ZIP file
 */
export const exportProjectToZip = async (
    project: Project,
    files: TranslationFile[]
): Promise<void> => {
    const zip = new JSZip();

    // Create project.json
    const projectJson: ProjectJson = {
        id: project.id,
        name: project.name,
        humanBenchmark: project.humanBenchmark,
        createdAt: project.createdAt instanceof Date
            ? project.createdAt.toISOString()
            : String(project.createdAt),
        lastModified: new Date().toISOString(),
        version: '1.0'
    };
    zip.file('project.json', JSON.stringify(projectJson, null, 2));

    // Create config.json
    const configJson: ConfigJson = {
        systemPrompt: project.systemPrompt,
        terminologyRules: project.terminologyRules
    };
    zip.file('config.json', JSON.stringify(configJson, null, 2));

    // Create files folder
    const filesFolder = zip.folder('files');
    if (filesFolder) {
        files
            .filter(f => f.projectId === project.id)
            .forEach((file, index) => {
                // Serialize file with proper date handling
                const fileData = {
                    ...file,
                    createdAt: file.createdAt instanceof Date
                        ? file.createdAt.toISOString()
                        : String(file.createdAt),
                    completedAt: file.completedAt instanceof Date
                        ? file.completedAt.toISOString()
                        : file.completedAt
                };
                const paddedIndex = String(index + 1).padStart(3, '0');
                filesFolder.file(`file_${paddedIndex}.json`, JSON.stringify(fileData, null, 2));
            });
    }

    // Generate ZIP and trigger download
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/**
 * Import a project from a ZIP file
 */
export const importProjectFromZip = async (file: File): Promise<ProjectBundle> => {
    const zip = await JSZip.loadAsync(file);

    // Read project.json
    const projectJsonFile = zip.file('project.json');
    if (!projectJsonFile) {
        throw new Error('Invalid project file: project.json not found');
    }
    const projectJsonStr = await projectJsonFile.async('string');
    const projectJson: ProjectJson = JSON.parse(projectJsonStr);

    // Read config.json
    const configJsonFile = zip.file('config.json');
    if (!configJsonFile) {
        throw new Error('Invalid project file: config.json not found');
    }
    const configJsonStr = await configJsonFile.async('string');
    const configJson: ConfigJson = JSON.parse(configJsonStr);

    // Generate new IDs to avoid conflicts
    const newProjectId = `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Construct Project object
    const project: Project = {
        id: newProjectId,
        name: projectJson.name,
        type: 'train', // Default imported projects to 'train' type
        systemPrompt: configJson.systemPrompt,
        terminologyRules: configJson.terminologyRules,
        humanBenchmark: projectJson.humanBenchmark,
        createdAt: new Date(projectJson.createdAt)
    };

    // Read files from files/ folder
    const files: TranslationFile[] = [];
    const filesFolder = zip.folder('files');
    if (filesFolder) {
        const fileEntries = Object.keys(zip.files)
            .filter(path => path.startsWith('files/') && path.endsWith('.json'));

        for (const filePath of fileEntries) {
            const fileEntry = zip.file(filePath);
            if (fileEntry) {
                const fileStr = await fileEntry.async('string');
                const fileData = JSON.parse(fileStr);

                // Generate new file ID and update projectId
                const newFileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

                files.push({
                    ...fileData,
                    id: newFileId,
                    projectId: newProjectId,
                    createdAt: new Date(fileData.createdAt),
                    completedAt: fileData.completedAt ? new Date(fileData.completedAt) : undefined
                });
            }
        }
    }

    return { project, files };
};
